import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HiveFundInvestFlow } from '../components/new-components/HiveFundInvest';
import { DealInvestFlow } from '../components/new-components/DealInvestFlow';
import { useUser } from '../contexts/UserContext';
import { Shield, ShieldOff, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/new-components/ui/button';
import api from '../services/api';
import toast from 'react-hot-toast';

/**
 * Generic offering invest page — reached via /portal/invest/offering/:slug
 * from the InvestCtaBlock in the Page Builder.
 *
 * Routes the user to the correct invest flow based on the slug.
 * KYC-gated: shows a block screen for non-verified users.
 */
const OfferingInvest = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { canInvest, kycStatus, user } = useUser();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const investCheck = canInvest();
  const accredStatus = user?.accreditation_status;
  const hasAccessToken = !!sessionStorage.getItem('investAccessToken');

  const handleRequestAccess = async () => {
    setSubmitting(true);
    try {
      const res: any = await api.post('/offering-access/request', {
        dealSlug: slug,
        message: message || undefined,
      });
      const d = res?.data || res;
      if (d.success !== false) {
        toast.success('Access request submitted. You will be notified when approved.');
        setSubmitted(true);
      } else {
        toast.error(d.message || 'Failed to submit request');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to submit request';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Accreditation gate — non-accredited users can request access
  if (accredStatus === 'self_declared_non_accredited' && !hasAccessToken) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card rounded-xl shadow-xl max-w-md w-full border border-border overflow-hidden">
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-center gap-3">
            <ShieldOff className="w-6 h-6 text-amber-600 shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Request Investment Access</h3>
              <p className="text-sm text-gray-600 capitalize">{slug?.replace(/-/g, ' ') || 'this offering'}</p>
            </div>
          </div>
          {submitted ? (
            <div className="px-6 py-8 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Send className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Request Submitted</h3>
              <p className="text-sm text-muted-foreground">
                An administrator will review your request. If approved, you'll receive a private investment link via email.
              </p>
              <Button variant="outline" onClick={() => navigate('/portal/invest/properties')} className="mt-4">
                Browse Offerings
              </Button>
            </div>
          ) : (
            <>
              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-gray-600">
                  As a non-accredited investor, you can request special access to invest in this offering.
                  An administrator will review your request and, if approved, send you a private investment link via email.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Share why you're interested in this offering..."
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                <Button variant="outline" onClick={() => navigate('/portal/invest/properties')}>
                  Cancel
                </Button>
                <button
                  onClick={handleRequestAccess}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit Request
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // KYC gate — block direct URL access for non-verified users
  if (!investCheck.canInvest) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card rounded-xl p-8 max-w-md w-full border border-border shadow-xl text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
            <Shield className="w-7 h-7 text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold">Identity Verification Required</h2>
          <p className="text-muted-foreground">
            To comply with federal securities regulations, you must complete identity
            verification (KYC) before investing.
          </p>
          <p className="text-sm text-muted-foreground">
            Current status:{' '}
            <span className="font-medium text-amber-500 capitalize">{kycStatus || 'not started'}</span>
          </p>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => navigate('/portal/invest/properties')}>
              Browse Offerings
            </Button>
            <Button className="flex-1" onClick={() => navigate('/portal/settings')}>
              Verify Identity
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Route to the correct invest flow by slug
  if (slug === 'hive-fund') {
    return (
      <HiveFundInvestFlow
        onBack={() => navigate('/portal/invest/properties')}
        onComplete={() => navigate('/portal/portfolio')}
        onGoToSettings={() => navigate('/portal/settings')}
      />
    );
  }

  // Generic deal invest flow for all other offerings
  return (
    <DealInvestFlow
      dealSlug={slug!}
      onClose={() => navigate('/portal/invest/properties')}
    />
  );
};

export default OfferingInvest;
